import React from 'react';
import { TradingPanel, TradingText } from '@trading-ui/core';
import { type QueryObserverResult, type RefetchOptions } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { MathUtils } from '@/utils/math.utils';

import type { ConsumptionChartResponse, ConsumptionTableResponse } from '@/interfaces';

import ConsumptionTable from '../../consumption-table/ConsumptionTable';

import * as styles from './TablePanel.css';

const getContainerStyles = (haveData: boolean): React.CSSProperties => ({
  backgroundColor: haveData ? '#F3BDFF' : 'var(--palette-background-paper)',
  height: '50px',
  borderEndEndRadius: '10px',
  borderEndStartRadius: '10px',
  boxShadow: 'var(--shadows-2)',
  display: 'flex',
  alignItems: 'center',
});

type Props = {
  tableData: ConsumptionTableResponse;
  isLoading: boolean;
  mutateChart: (options?: RefetchOptions) => Promise<QueryObserverResult<ConsumptionChartResponse, Error>>;
  mutateTable: (options?: RefetchOptions) => Promise<QueryObserverResult<ConsumptionTableResponse, Error>>;
};

export default function TablePanel({ tableData, isLoading, mutateChart, mutateTable }: Props) {
  const { t } = useTranslation();

  return (
    <motion.div
      className={styles.root}
      initial={{ x: '-100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}>
      <TradingPanel className={styles.panel}>
        <ConsumptionTable data={tableData} isLoading={isLoading} mutateChart={mutateChart} mutateTable={mutateTable} />
      </TradingPanel>
      <div style={getContainerStyles(Boolean(tableData))}>
        {tableData && (
          <div className={styles.averagesContent}>
            <TradingText
              text={`${t('consumer.tablePanel.averages.nomination')}: ${MathUtils.formatNumber(
                tableData.meta.averages?.nomination ?? 0,
                2,
                2,
                true
              )}`}
              variant="h6"
              style={{ fontSize: '12px', fontWeight: 'bold' }}
            />
            <TradingText
              text={`${t('consumer.tablePanel.averages.enagas')}: ${MathUtils.formatNumber(
                tableData.meta.averages?.enagas ?? 0,
                2,
                2,
                true
              )}`}
              variant="h6"
              style={{ fontSize: '12px', fontWeight: 'bold' }}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
}
